import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Eye, Bookmark, ArrowRight, Book, Briefcase, GraduationCap, Award, FileText, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import './PopularThisWeek.css';
import { useTranslation } from 'react-i18next';
import { isFeatureEnabled } from '../config/studentHubFeatureFlags';
import { getApiBase } from '../api/httpClient';

// iter300 — Use `getApiBase()` so the production build (wassla.tech)
// hits the same-origin proxy instead of the stale build-time URL that
// points at the preview backend (caused CORS failures + "Failed to fetch").
const apiUrl = (path) => `${getApiBase()}${path}`;

// Map category → feature-flag slug so disabled modules are filtered out
// even when legacy rows still exist in the DB.
const CATEGORY_FEATURE_SLUG = {
  books_notes: 'books',
  internships_jobs: 'internships',
  scholarships: 'scholarships',
  universities_admissions: 'admissions',
  courses_learning: 'courses',
  cv_templates: 'cv',
};

const isCategoryEnabled = (cat) => {
  const slug = CATEGORY_FEATURE_SLUG[cat];
  if (!slug) return true; // unknown → don't filter
  return isFeatureEnabled(slug);
};

// Category icon mapping
const CATEGORY_ICONS = {
  books_notes: Book,
  internships_jobs: Briefcase,
  scholarships: Award,
  universities_admissions: GraduationCap,
  courses_learning: BookOpen,
  cv_templates: FileText,
};

// iter293 — Item-DETAIL routes MUST match the registered routes in
// App.js. The previous mapping `/student/books` pointed at the LIST
// page (which does not accept `/:id`), so clicking any popular card
// resolved to `/student/books/<id>` → no matching route → blank white
// page on production.  The canonical detail-page routes are the
// `/student-hub/<slug>/:id` ones registered in App.js.
const CATEGORY_DETAIL_ROUTES = {
  books_notes: '/student-hub/books',
  internships_jobs: '/student-hub/opportunities',
  scholarships: '/student-hub/opportunities',
  // courses + admissions detail pages aren't registered yet — fall back
  // to their list page until they ship.
};

// List/index pages — used by "View All" + fallback when a detail
// route is missing.
const CATEGORY_LIST_ROUTES = {
  books_notes: '/student-hub/books',
  internships_jobs: '/student-hub/internships',
  scholarships: '/student-hub/scholarships',
  universities_admissions: '/student-hub',
  courses_learning: '/student-hub',
  cv_templates: '/student-hub/cv',
};

// Category colors
const CATEGORY_COLORS = {
  books_notes: '#1F6F3A',
  internships_jobs: '#0284C7',
  scholarships: '#DC2626',
  universities_admissions: '#7C3AED',
  courses_learning: '#EA580C',
  cv_templates: '#0891B2',
};

/**
 * Popular This Week Section
 * Displays trending items from the Student Hub
 */
export default function PopularThisWeek({ lang = 'en', className = '' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRTL = lang === 'ar';
  
  const [items, setItems] = useState([]);
  const [totalBooks, setTotalBooks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch popular items
  const fetchPopular = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to get popular items from the API
      // iter293 — Request up to 12 items. Backend tops up with newest
      // book listings when popularity data is sparse, so this returns
      // a meaningful list even right after a fresh book publish.
      const response = await fetch(apiUrl(`/api/student-hub/popular?limit=12&days=7`));
      
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      // iter298 — `total_books` is the canonical REAL total (after
      // seed/test/admin-test exclusions). Surface it for the "View all (N)"
      // badge so the count matches what the Books page will actually show.
      if (typeof data.total_books === 'number') {
        setTotalBooks(data.total_books);
      }
      
      // If we have popular items, use them (filtered by enabled features)
      if (data.items && data.items.length > 0) {
        setItems(data.items.filter(it => isCategoryEnabled(it.category)));
      } else {
        // Fallback: Get a mix of items from enabled categories only.
        // iter293 — fetch 4 per category, then sort + keep top 12.
        const categories = ['books_notes', 'internships_jobs', 'scholarships']
          .filter(isCategoryEnabled);
        const fallbackItems = [];
        
        for (const category of categories) {
          try {
            const catResponse = await fetch(apiUrl(`/api/student-hub/items?category=${category}&limit=4&sort=newest`));
            if (catResponse.ok) {
              const catData = await catResponse.json();
              if (catData.items) {
                fallbackItems.push(...catData.items.slice(0, 4));
              }
            }
          } catch (e) {
            console.debug(`[PopularThisWeek] Failed to fetch ${category}:`, e);
          }
        }
        
        // Sort by views7d + saves7d*2, then by createdAt as tiebreaker.
        fallbackItems.sort((a, b) => {
          const scoreA = (a.views7d || 0) + (a.saves7d || 0) * 2;
          const scoreB = (b.views7d || 0) + (b.saves7d || 0) * 2;
          if (scoreB !== scoreA) return scoreB - scoreA;
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        });
        
        setItems(fallbackItems.slice(0, 12));
      }
    } catch (err) {
      console.error('[PopularThisWeek] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchPopular();
  }, [fetchPopular]);
  
  // Handle item click
  const handleItemClick = useCallback((item) => {
    // iter293 — Resolve to the canonical detail route. If a category
    // doesn't have a registered detail route yet, route to its list
    // page rather than a 404/blank page.
    const detailRoute = CATEGORY_DETAIL_ROUTES[item.category];
    if (!detailRoute) {
      const listRoute = CATEGORY_LIST_ROUTES[item.category] || '/student-hub/books';
      navigate(listRoute, { state: { from: '/student-hub' } });
      return;
    }
    if (!item.id) {
      // Defensive — if for any reason the id is missing, send the user
      // to the list page instead of a blank screen.
      const listRoute = CATEGORY_LIST_ROUTES[item.category] || '/student-hub/books';
      navigate(listRoute, { state: { from: '/student-hub' } });
      return;
    }
    navigate(`${detailRoute}/${item.id}`, { state: { from: '/student-hub' } });
  }, [navigate]);
  
  // Handle view all click — list page, NOT a detail page.
  const handleViewAll = useCallback(() => {
    navigate('/student-hub/books', { state: { from: '/student-hub' } });
  }, [navigate]);
  
  // Loading state
  if (loading) {
    return (
      <section className={`popular-this-week ${className}`} data-testid="popular-this-week">
        <div className="popular-header">
          <div className="popular-title-row">
            <TrendingUp className="popular-icon" />
            <h2 className="popular-title">
              {t('ptw.popularThisWeek')}
            </h2>
          </div>
        </div>
        <div className="popular-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="popular-card-skeleton">
              <div className="skeleton-image" />
              <div className="skeleton-content">
                <div className="skeleton-line long" />
                <div className="skeleton-line short" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  
  // Error or no items
  if (error || items.length === 0) {
    return null; // Gracefully hide if no data
  }
  
  // iter293 — Operator-spec Option A: show up to 5 popular items on
  // the Student Hub landing with proper spacing; the "View All" button
  // already routes to the full Books list so nothing is hidden — any
  // listing beyond the top 5 is still reachable via View All.
  const PREVIEW_LIMIT = 5;
  const previewItems = items.slice(0, PREVIEW_LIMIT);
  // iter298 — Badge count prefers `total_books` from the canonical
  // public-book query; falls back to `items.length` only when the API
  // hasn't returned the new field yet (older backend).
  const realBadgeCount = totalBooks > 0 ? totalBooks : items.length;
  const hasMore = realBadgeCount > PREVIEW_LIMIT;

  return (
    <section className={`popular-this-week ${className}`} dir={isRTL ? 'rtl' : 'ltr'} data-testid="popular-this-week">
      <div className="popular-header">
        <div className="popular-title-row">
          <TrendingUp className="popular-icon" />
          <h2 className="popular-title">
            {t('ptw.popularThisWeek')}
          </h2>
        </div>
        <button 
          className="popular-view-all"
          onClick={handleViewAll}
          data-testid="popular-view-all-btn"
          aria-label={isRTL ? 'عرض الكل' : 'View All'}
        >
          {hasMore
            ? (isRTL ? `عرض الكل (${realBadgeCount})` : `Show all ${realBadgeCount}`)
            : t('ptw.viewAll')}
          {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>
      
      <div className="popular-grid" data-testid="popular-grid">
        {previewItems.map((item) => {
          const IconComponent = CATEGORY_ICONS[item.category] || Book;
          const categoryColor = CATEGORY_COLORS[item.category] || '#1F6F3A';
          const title = isRTL && item.title_ar ? item.title_ar : item.title;
          const subtitle = isRTL && item.subtitle_ar ? item.subtitle_ar : item.subtitle;
          
          return (
            <article 
              key={item.id} 
              className="popular-card"
              onClick={() => handleItemClick(item)}
              data-testid={`popular-card-${item.id}`}
            >
              {/* Category Badge */}
              <div 
                className="popular-card-badge"
                style={{ backgroundColor: categoryColor }}
              >
                <IconComponent size={12} />
              </div>
              
              {/* Content */}
              <div className="popular-card-content">
                <h3 className="popular-card-title">{title}</h3>
                {subtitle && (
                  <p className="popular-card-subtitle">{subtitle}</p>
                )}
                
                {/* Stats */}
                <div className="popular-card-stats">
                  <span className="stat">
                    <Eye size={12} />
                    {item.views7d || 0}
                  </span>
                  <span className="stat">
                    <Bookmark size={12} />
                    {item.saves7d || 0}
                  </span>
                  {item.price !== undefined && item.price > 0 && (
                    <span className="stat price">
                      {item.price} {item.currency || 'SDG'}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Arrow indicator */}
              <div className="popular-card-arrow">
                {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
